import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GlassCard } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demo, setDemo] = useState({ enabled: false, accounts: [] });
  const [hideKeyboard, setHideKeyboard] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/auth/demo-accounts`)
      .then((r) => setDemo(r.data || { enabled: false, accounts: [] }))
      .catch(() => setDemo({ enabled: false, accounts: [] }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    // Hide keyboard before attempting login to improve UX
    setHideKeyboard(true);
    document.activeElement?.blur();

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/', { replace: true });
    } else {
      // Re-show keyboard on auth failure so user can correct credentials
      setError(result.error);
      setHideKeyboard(false);
      // Refocus first input to help user continue
      setTimeout(() => document.getElementById('email')?.focus(), 100);
    }
    setLoading(false);
  };

  // Auto-submit when demo account is selected
  const handleDemoAccountSelect = async (account) => {
    setEmail(account.email);
    setPassword(account.password);
    setError('');
    setHideKeyboard(true); // Hide keyboard during auto-submit
    
    // Give a tiny delay to ensure state is updated before login attempt
    setTimeout(async () => {
      setLoading(true);
      const result = await login(account.email, account.password);
      
      if (result.success) {
        navigate('/', { replace: true });
      } else {
        setError(result.error);
        setHideKeyboard(false); // Re-show keyboard on auth failure
      }
      setLoading(false);
    }, 50);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-primary/20 flex items-center justify-center mx-auto mb-4">
            <span className="font-arabic text-3xl text-primary">ن</span>
          </div>
          <h1 className="text-2xl font-serif text-text-primary mb-2">{t('auth.login_title')}</h1>
        </div>

        <GlassCard>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 rounded-lg bg-danger/20 border border-danger/50 text-danger text-sm">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-text-primary">{t('auth.email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setHideKeyboard(false); // Re-enable keyboard on manual edit
                }}
                placeholder="votre@email.com"
                required
                readOnly={hideKeyboard}
                className="bg-surface border-border"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-text-primary">{t('auth.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setHideKeyboard(false); // Re-enable keyboard on manual edit
                  }}
                  placeholder="••••••••"
                  required
                  readOnly={hideKeyboard}
                  className="bg-surface border-border pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-background hover:bg-primary-hover"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('auth.submit_login')}
            </Button>
          </form>

          <div className="mt-4 text-right text-sm">
            <Link to="/forgot-password" className="text-primary hover:underline" data-testid="login-forgot-password-link">
              Mot de passe oublié ?
            </Link>
          </div>

          <div className="mt-4 text-center text-sm text-text-secondary">
            {t('auth.no_account')}{' '}
            <Link to="/register" className="text-primary hover:underline">
              {t('auth.submit_register')}
            </Link>
          </div>
        </GlassCard>

        {demo.enabled && demo.accounts?.length > 0 && (
          <GlassCard className="mt-4 border-amber-500/30" data-testid="demo-accounts-panel">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <h3 className="font-serif text-sm text-amber-300">Comptes de démonstration (temporaire)</h3>
            </div>
            <p className="text-xs text-text-muted mb-3">
              Cliquez sur un rôle pour pré-remplir le formulaire. Ces comptes seront supprimés en production.
            </p>
            <div className="grid grid-cols-1 gap-2">
              {demo.accounts.map((a) => (
                <button
                  key={a.email}
                  type="button"
                  onClick={() => handleDemoAccountSelect(a)}
                  disabled={loading}
                  className="flex items-center justify-between gap-3 p-2 rounded border border-border
                             hover:bg-surface-elevated transition-colors text-left text-xs disabled:opacity-50"
                  data-testid={`demo-account-${a.role}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-text-primary">{a.label}</div>
                    <div className="text-text-muted font-mono truncate">{a.email}</div>
                  </div>
                  <span className="shrink-0 px-2 py-0.5 rounded bg-primary/10 text-primary border border-primary/20">
                    {a.role}
                  </span>
                </button>
              ))}
            </div>
          </GlassCard>
        )}
      </motion.div>
    </div>
  );
};

export default Login;
